// Public UI adapted from Klargo-Reference/lib/format.ts.
import i18n from 'i18next';
export const isEnglishLocale = (language) => {
    if (!language)
        return false;
    return language === 'en' || language.startsWith('en-') || language.includes('English');
};
// Same output as the server (PriceFormat.Sek), so the screen, the PDF and the BankID text read
// alike: "125 000 kr" (sv) / "125,000 kr" (en), whole kronor, plain space.
export const formatSEK = (amount) => formatSekAmount(amount, 'sv');
export const formatSekAmount = (amount, language) => {
    const n = typeof amount === 'number' ? amount : Number(String(amount ?? '').replace(/[\s\u00A0\u202F]/g, '').replace(',', '.'));
    if (amount == null || amount === '' || !Number.isFinite(n))
        return '';
    const lang = language || i18n.language || 'sv';
    return Math.round(n).toLocaleString(isEnglishLocale(lang) ? 'en-US' : 'sv-SE', { maximumFractionDigits: 0 })
        .replace(/[\u00A0\u202F]/g, ' ') + ' kr';
};
export const formatRegNumber = (regNumber) => {
    const clean = (regNumber || '').replace(/[,\s]+$/, '');
    return clean.replace(/^([A-ZÅÄÖ]+)(\d+)$/, '$1 $2');
};
export const normalizeAccountNumber = (account) => account.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
export const formatAccountNumber = (account) => {
    const normalized = normalizeAccountNumber(account);
    return normalized.replace(/(.{4})/g, '$1 ').trim();
};
export const maskAccountNumber = (account) => {
    const normalized = normalizeAccountNumber(account);
    if (normalized.length <= 4)
        return normalized;
    return `•••• •••• •••• ${normalized.slice(-4)}`;
};
export const formatOdometer = (km, locale = 'sv') => {
    if (typeof km !== 'number')
        return '';
    const isSvenska = !isEnglishLocale(locale);
    const formattedKm = isSvenska
        ? km.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : km.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    if (isSvenska) {
        const mil = Math.round(km / 10);
        const formattedMil = mil.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return `${formattedMil} mil (${formattedKm} km)`;
    }
    return `${formattedKm} km`;
};
export const formatHistoricMileage = (km, language = 'sv') => {
    if (typeof km !== 'number')
        return '';
    if (isEnglishLocale(language)) {
        return `${km.toLocaleString('en-GB', { maximumFractionDigits: 0 })} km`;
    }
    return `${Math.round(km / 10).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} mil`;
};
export const formatDateShort = (dateStr, language = 'sv') => {
    if (!dateStr)
        return '';
    try {
        return new Date(dateStr).toLocaleDateString(isEnglishLocale(language) ? 'en-GB' : 'sv-SE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }
    catch {
        return '';
    }
};
export const formatDateLocalized = (dateStr, language = 'sv') => {
    if (!dateStr)
        return '';
    try {
        return new Date(dateStr).toLocaleDateString(isEnglishLocale(language) ? 'en-GB' : 'sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }
    catch {
        return '';
    }
};
export const formatAgreedDateTime = (dateTimeStr, language = 'sv') => {
    if (!dateTimeStr)
        return '';
    const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(dateTimeStr);
    if (!parts)
        return formatDateLocalized(dateTimeStr, language);
    const [, year, month, day, hour, minute] = parts;
    const date = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(isEnglishLocale(language) ? 'en-GB' : 'sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
    return `${date} · ${hour}:${minute}`;
};
export const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr)
        return '';
    try {
        const date = new Date(dateTimeStr);
        return date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    catch {
        return '';
    }
};
const SV_MONTHS = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatSignedAt = (iso, english) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '';
    const months = english ? EN_MONTHS : SV_MONTHS;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};
